import { isInIframe } from '@shared/utils/isInIframe';
import type { EventDesignFragment } from '@graphql/fragments/eventDesign.graphql.generated';
import type { EventDesignPreferenceRangeFragment } from '@graphql/fragments/eventDesignPreferences.graphql.generated';
import { deriveWebsiteColorsFromEventDesign, parseDesignPreferences } from '@shared/utils/websiteDesign';
import { googleFontFamilies } from '../../common/fonts';
import type { GuestSiteEventDesign, LayoutConfigBrannan, LayoutThemeOverrides, ApplicatorConfigVariantsBrannan } from '../layout.types';
import chroma from 'chroma-js';
import { useStyleApplicator } from '../../common/utils/useStyleApplicator';
import type { Theme } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useCallback, useMemo, useState } from 'react';
import globalWindow from '@shared/core/globals';
import type { Colors } from '@withjoy/joykit/theme';
import { createGlobalStyle } from '@withjoy/joykit/theme';
import memoizeOne from 'memoize-one';
import type { CreateThemeArgs } from '@withjoy/joykit/theme';
import { createTheme } from '@withjoy/joykit/theme';
import { createTextStyleVariantBuilder, pxToRem } from '@shared/joykit/packages/core/common/themes/shared/typography';
import { mergeTypograhyThemeOverrides } from '../../common/theming';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { getActiveColor, getHoverColor } from '../layout.utils';

const isIframed = isInIframe();

const BRANNAN_THEME_DEFAULTS: CreateThemeArgs['defaults'] = {
  breakpointWidths: [isIframed ? 375 : 480, 960, 1024, 1440, 1920],
};

export const getBrannanMobileMediaQuery = (theme: Theme) => theme.mediaQueries.between(0, { breakpointAlias: 'sm4' });

const generateThemableColorValues = memoizeOne((color?: string): DeepPartial<Colors> => {
  if (color) {
    const colorHover = getHoverColor(color);
    const colorActive = getActiveColor(color);

    return {
      linkVisited: chroma(color).darken(0.3).hex(),
      linkText: color,
      linkHover: colorHover,
      linkActive: colorActive,

      buttonPrimaryText: '#FFFFFF',
      buttonPrimaryFill: color,
      buttonPrimaryHover: colorHover,
      buttonPrimaryActive: colorActive,

      scrollbarHandle: `rgba(${chroma(color).alpha(0.5).rgba().join(', ')})`,

      buttonOutlinePrimaryText: color,
      buttonOutlinePrimaryTextHover: colorHover,
      buttonOutlinePrimaryTextPress: colorActive,
      buttonOutlinePrimaryFillHover: chroma(color).alpha(0.1).css(),
      buttonOutlinePrimaryFillPress: chroma(color).alpha(0.2).css(),
    };
  }
  return {};
});

export const getBrannanThemeOverrides = (overrides: LayoutThemeOverrides = {}): DeepPartial<Theme> => {
  const {
    eventFontFamily = googleFontFamilies.greatVibes,
    bodyFontFamily = googleFontFamilies.notoSerif,
    hedFontFamily = googleFontFamilies.notoSerif,
    textTransform = 'none',
  } = overrides.typography || {};
  const { primaryFillColor } = overrides.colors || {};

  const themableColors: DeepPartial<Colors> = generateThemableColorValues(primaryFillColor);

  const buildVariantBody = createTextStyleVariantBuilder(bodyFontFamily);
  const buildVariantHed = createTextStyleVariantBuilder(hedFontFamily);

  const typography = mergeTypograhyThemeOverrides({
    variants: {
      body4: buildVariantBody(400, 17, 1.5, 0),
      label2: buildVariantBody(400, 13, 1.4, 0),

      hed2: buildVariantHed(700, 17, 1.36, 0.02),
      hed3: buildVariantHed(500, 20, 1.36, 0),
      hed4: {
        ...buildVariantHed(400, 20, 1.4, 0, { casing: 'uppercase' }),
        letterSpacing: '0.16em',
      },
      // same font-family as aloha (default themeing.ts file)
      hed5: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
      },
      // same font-family as aloha (default themeing.ts file)
      hed6: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },

      display1: {
        fontFamily: eventFontFamily,
        lineHeight: 1.23,
        textTransform,
      },
      display2: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display3: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display4: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display5: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display6: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display7: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display8: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display9: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display10: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display11: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
    },
  });

  return {
    typography,
    colors: {
      ...themableColors,
    },
  };
};

export const brannanConfig: LayoutConfigBrannan = {
  applicatorHtmlProps: {
    layerMiddle: {
      prop: { 'data-layer': 'middle' },
      selector: '[data-layer="middle"]',
    },
    layerBack: {
      prop: { 'data-layer': 'back' },
      selector: '[data-layer="back"]',
    },
    componentTitleBanner: {
      prop: { 'data-component': 'title-banner' },
      selector: '[data-component="title-banner"]',
    },
  },
  theme: createTheme({ defaults: BRANNAN_THEME_DEFAULTS, overrides: getBrannanThemeOverrides() }), // extendTheme(getBrannanThemeOverrides()),
  configureThemeOverrides: eventDesign => {
    const { font } = eventDesign;
    const { accentColor } = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);
    const overrides = getBrannanThemeOverrides({
      typography: { eventFontFamily: font.fontFamily, textTransform: font.textTransform },
      colors: { primaryFillColor: accentColor },
    });
    return createTheme({ defaults: BRANNAN_THEME_DEFAULTS, overrides });
  },
  fonts: {
    google: ['greatVibes', 'notoSerif'],
  },
  previewEventHandlers: {
    onTemplateChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.welcome) {
        history.push({ pathname: eventPageRoutes.welcome.path });
      }
      globalWindow.scrollTo?.(0, 0);
    },
    onLayoutChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.welcome) {
        history.push({ pathname: eventPageRoutes.welcome.path });
      }
      globalWindow.scrollTo?.(0, 0);
    },
    onThemeChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onFontChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onGraphicAccentChange: () => {
      const el = globalWindow.document?.getElementById('graphic-accent');
      el?.scrollIntoView();
    },
    onPrimaryFillColorChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.schedule) {
        // Navigate to schedule page and scroll to schedule section
        // If schedule element exists, scroll smoothly to it
        // Otherwise, listen for route change, wait for element to render, then scroll

        const el = globalWindow.document?.getElementById('schedule');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          const historySub = history.listen(() => {
            globalWindow.setTimeout(() => {
              const el = globalWindow.document?.getElementById('schedule');
              el?.scrollIntoView();
            }, 500);
            historySub();
          });
          history.push({ pathname: eventPageRoutes.schedule.path });
          return [historySub];
        }
      }
      return [];
    },
  },
};

////////////////////////////////////////////////////////////////

const _isBackgroundTransparencyEnabled = (
  preference?: EventDesignPreferenceRangeFragment,
  websiteLayoutBrannanSettings?: EventDesignFragment['theme']['websiteLayoutBrannanSettings'],
  isCustomArtEnabled?: boolean,
) => {
  const brannanBackgroundTransparency = websiteLayoutBrannanSettings?.brannanBackgroundTransparency;
  // For custom art, enable background transparency irrespective of support for customization
  return brannanBackgroundTransparency?.supportsCustomization || isCustomArtEnabled
    ? preference
      ? preference.rangeValue < 1
      : false
    : brannanBackgroundTransparency
      ? brannanBackgroundTransparency.rangeValue <= 0.7
      : false;
};

export const getBrannanApplicatorConfig = (eventDesign: NonNullable<GuestSiteEventDesign>, isBypassWebsiteThemeCustomizationEnabled: boolean): ApplicatorConfigVariantsBrannan => {
  const { brannanBackgroundTransparency, brannanInnerFrameBorder } = parseDesignPreferences(eventDesign.designPreferences);

  const derivedEventColors = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);

  const artBackgroundFontColor = chroma(derivedEventColors.artBackgroundFontColor).rgba();
  const artBackgroundColor = chroma(derivedEventColors.artBackgroundColor).rgba();

  // Note that we check isAlohaGuestPage inside brannan because we use Brannan layout on the guest registry
  // page no matter which template is chosen. See https://withjoy.atlassian.net/browse/COMMERCE-284
  const applicatorConfig: ApplicatorConfigVariantsBrannan = {
    layerBack: {
      customBackgroundColor: {
        isEnabled: true,
        textColor: artBackgroundFontColor,
        backgroundColor: artBackgroundColor,
      },
    },
    layerMiddle: {
      border: {
        isEnabled: brannanInnerFrameBorder?.booleanValue ?? true,
      },
      backgroundTranslucency: {
        isEnabled: _isBackgroundTransparencyEnabled(brannanBackgroundTransparency, eventDesign.theme?.websiteLayoutBrannanSettings, eventDesign.useCustomTheme),
      },
    },
    componentTitleBanner: {
      customBackgroundColor: {
        isEnabled: true,
        // NOTE: `title-banner` component will have its own text color in the future
        textColor: artBackgroundFontColor,
        backgroundColor: artBackgroundColor,
        bypassThemeCustomization: isBypassWebsiteThemeCustomizationEnabled,
      },
    },
  };

  return applicatorConfig;
};
////////////////////////////////////////////////////////////////

export const useBrannanDesign = (_eventHandle: string, eventDesign: GuestSiteEventDesign) => {
  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const styledApplicatorLoading = useFeatureValue('enableLoadingStyleApplicator');
  const [isBypassWebsiteThemeCustomizationEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'bypassWebsiteThemeCustomization' });

  const { customCss } = eventDesign || {};
  // useResponsive is SSR-aware (derives default from defaultScreen context).
  // sm4 = 960px — isSmallScreen is true when viewport ≤ 960px.
  const [isSmallScreen] = useResponsive({ values: { _: true, sm4: false } });
  const themeId = eventDesign?.theme.themeId;

  // Incrementing designVersion forces useStyleApplicator to re-run.
  // Used by lazy-loaded header components to re-apply sprites after mount.
  const [designVersion, setDesignVersion] = useState(0);
  const reapplyDesign = useCallback(() => setDesignVersion(v => v + 1), []);

  // Get applicator config based on user design preferences
  const applicatorConfig = useMemo(() => {
    return eventDesign ? getBrannanApplicatorConfig(eventDesign, isBypassWebsiteThemeCustomizationEnabled) : undefined;
  }, [eventDesign, isBypassWebsiteThemeCustomizationEnabled]);

  const { loading } = useStyleApplicator(
    styleApplicator => {
      if (themeId) {
        const { layerBack, layerMiddle, componentTitleBanner } = brannanConfig.applicatorHtmlProps;

        // On small screens, apply styles to header
        if (isSmallScreen) {
          return [styleApplicator.applyComponentSprites(componentTitleBanner.selector, themeId, 'title-banner', applicatorConfig?.componentTitleBanner)];
        }

        // Apply styles to frames
        return [
          styleApplicator.applyCompositionSprites(layerBack.selector, themeId, 'stacked', 'back', applicatorConfig?.layerBack),
          styleApplicator.applyCompositionSprites(layerMiddle.selector, themeId, 'stacked', 'middle', applicatorConfig?.layerMiddle),
        ];
      }
      return [];
    },
    // designVersion is a trigger dep — not referenced in the callback, but forces a re-run
    // when lazy-loaded components call reapplyDesign() after mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeId, isSmallScreen, applicatorConfig, designVersion],
  );

  return { isSmallScreen, applicatorConfig, reapplyDesign, loading: styledApplicatorLoading ? loading : false, CustomStyle: customCss ? createGlobalStyle`${customCss}` : null };
};
